import * as React from 'react'

import { TableProvider, useTable } from '@/modules/drivers/context/TableContext'
import CompaniesTable from '../components/CompaniesTable'
import CompanyBasicActionBar from '../components/CompanyBasicActionBar'
import CompanySelectedActionBar from '../components/CompanySelectedActionBar'
import ManualCompanyModal from '../components/ManualCompanyModal'
import EditCompanyModal from '../components/EditCompanyModal'
import CompanyGroupPicker from '../components/CompanyGroupPicker'
import { CompanyDetailsModel } from '@/app/models/companyDetailsModel'
import { CompanyNameBadge } from '@/modules/drivers/shared/viewBars/CompanyNameBadge'
import { CompanyGroupModel } from '../types/companyGroups'
import { RouteComponentProps } from '@/app/navigation/withRouter'

interface AddCompanyPageRouteParams {
  companyId: string
}

interface ManualTabContentProps {
  companyId: string
  selectedGroupFilter: CompanyGroupModel | null
}

const ManualTabContent: React.FC<ManualTabContentProps> = ({
  companyId,
  selectedGroupFilter,
}) => {
  const { isSelectedAny, selectedGroup, resetAll } = useTable()
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false)
  const [editedCompanyId, setEditedCompanyId] = React.useState<string | null>(
    null,
  )

  const openEdit = React.useCallback((company: CompanyDetailsModel) => {
    setEditedCompanyId(String(company.id))
  }, [])

  const openEditFromBulk = React.useCallback(() => {
    if (selectedGroup?.ids.length === 1) {
      setEditedCompanyId(String(selectedGroup.ids[0]))
    }
  }, [selectedGroup])

  return (
    <>
      <div className="tw-flex tw-flex-none tw-flex-col">
        {isSelectedAny ? (
          <CompanySelectedActionBar
            companyId={companyId}
            selectedGroup={selectedGroup}
            resetAll={resetAll}
            onEdit={
              selectedGroup?.ids.length === 1 ? openEditFromBulk : undefined
            }
          />
        ) : (
          <CompanyBasicActionBar
            companyId={companyId}
            onOpenCreateModal={() => setIsCreateModalOpen(true)}
          />
        )}
      </div>
      <CompaniesTable
        companyId={companyId}
        selectedGroupFilter={selectedGroupFilter}
        onEdit={openEdit}
      />
      <ManualCompanyModal
        isOpen={isCreateModalOpen}
        close={() => setIsCreateModalOpen(false)}
        companyId={companyId}
      />
      <EditCompanyModal
        isOpen={editedCompanyId !== null}
        close={() => setEditedCompanyId(null)}
        editedCompanyId={editedCompanyId}
      />
    </>
  )
}

export const AddCompanyPage: React.FC<
  RouteComponentProps<AddCompanyPageRouteParams>
> = ({ match }) => {
  const { companyId } = match.params
  const [selectedGroupFilter, setSelectedGroupFilter] =
    React.useState<CompanyGroupModel | null>(null)

  return (
    <div className="tw-flex tw-h-full tw-flex-col">
      <div className="tw-flex tw-items-center tw-gap-4 tw-border-b tw-p-4">
        <CompanyNameBadge />
        <CompanyGroupPicker
          companyId={companyId}
          onSelect={setSelectedGroupFilter}
          actualGroup={selectedGroupFilter}
        />
      </div>

      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col ">
        <TableProvider bulk={true}>
          <ManualTabContent
            companyId={companyId}
            selectedGroupFilter={selectedGroupFilter}
          />
        </TableProvider>
      </div>
    </div>
  )
}
